import { useState } from 'react';

import styled, { css } from 'styled-components';
import {
	ITrackingCreatorRankingDB,
	IRankingFilterParams,
	ICreatorRankingFilterParams,
} from 'type/tracking';

import TableCell from 'components/common/table/cell/TableCell';
import SimpleTableFooter from 'components/SimpleTableFooter';
import { useGetCreatorRanking } from 'hooks/queries/tracking';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreImg from 'shared/CoreImg';
import CoreLoading from 'shared/CoreLoading';
import CoreSelect from 'shared/CoreSelect';
import CoreSvg from 'shared/CoreSvg';
import CoreTooltip from 'shared/CoreTooltip';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';

import CreatorContentsAccordion from './CreatorContentsAccordion';

interface ICreatorRankingTable {
	code: string;
	filter: IRankingFilterParams;
}

const VIEW_MODE_OPTIONS = ['평균', '누적'];
const VIEW_MODE_MAP: Record<string, 'AVERAGE' | 'TOTAL'> = {
	평균: 'AVERAGE',
	누적: 'TOTAL',
};

const PAGE_SIZE_OPTIONS = ['25개씩 보기', '50개씩 보기', '100개씩 보기'];
const PAGE_SIZE_MAP: Record<string, number> = {
	'25개씩 보기': 25,
	'50개씩 보기': 50,
	'100개씩 보기': 100,
};
const PAGE_SIZE_REVERSE: Record<number, string> = {
	25: '25개씩 보기',
	50: '50개씩 보기',
	100: '100개씩 보기',
};

const COLUMN_WIDTHS = {
	rank: 40,
	creator: 150,
	followers: 90,
	contentCount: 70,
	contentTypes: 130,
	viewCount: 100,
	likeCount: 100,
	commentCount: 95,
	shareCount: 95,
	engagement: 110,
	expand: 20,
};

const CONTENT_TYPE_SHORT_MAP: Record<string, string> = {
	ig_feed: '피드',
	ig_video: '릴스',
	yt_video: '롱폼',
	yt_shorts: '쇼츠',
	tt_content: '틱톡',
};

const PLATFORM_ICON_MAP: Record<string, JSX.Element> = {
	INSTAGRAM: <Icon.Insta />,
	YOUTUBE: <Icon.Youtube />,
	TIKTOK: <Icon.Tiktok />,
	THREADS: <Icon.Insta />,
};

// API orderBy 매핑
const getCreatorOrderBy = (field: string, viewMode: 'AVERAGE' | 'TOTAL', direction: 'desc' | 'asc'): string => {
	const prefix = viewMode === 'AVERAGE' ? 'avg' : 'total';
	const suffix = direction === 'desc' ? 'Desc' : 'Asc';
	const map: Record<string, string> = {
		viewCount: `${prefix}View${suffix}`,
		likeCount: `${prefix}Like${suffix}`,
		commentCount: `${prefix}Comment${suffix}`,
		engagement: `${prefix}Engagement${suffix}`,
		followers: `follower${suffix}`,
		contentCount: `contentCount${suffix}`,
	};
	return map[field] ?? `${prefix}View${suffix}`;
};

type FlexType = 'start' | 'center' | 'end' | 'between';

interface IHeaderItem {
	header: string;
	name: string;
	width: number;
	align: FlexType;
	sortable?: boolean;
	prefixed?: boolean;
}

const BASE_HEADER_ITEMS: IHeaderItem[] = [
	{ header: '', name: 'expand', width: COLUMN_WIDTHS.expand, align: 'center' },
	{ header: '순위', name: 'rank', width: COLUMN_WIDTHS.rank, align: 'center' },
	{
		header: '계정/채널',
		name: 'creator',
		width: COLUMN_WIDTHS.creator,
		align: 'start',
	},
	{
		header: '팔로워 수',
		name: 'followers',
		width: COLUMN_WIDTHS.followers,
		align: 'end',
		sortable: true,
	},
	{
		header: '콘텐츠 수',
		name: 'contentCount',
		width: COLUMN_WIDTHS.contentCount,
		align: 'end',
	},
	{
		header: '콘텐츠 유형',
		name: 'contentTypes',
		width: COLUMN_WIDTHS.contentTypes,
		align: 'start',
	},
	{
		header: '조회 수',
		name: 'viewCount',
		width: COLUMN_WIDTHS.viewCount,
		align: 'end',
		sortable: true,
		prefixed: true,
	},
	{
		header: '좋아요 수',
		name: 'likeCount',
		width: COLUMN_WIDTHS.likeCount,
		align: 'end',
		sortable: true,
		prefixed: true,
	},
	{
		header: '댓글 수',
		name: 'commentCount',
		width: COLUMN_WIDTHS.commentCount,
		align: 'end',
		sortable: true,
		prefixed: true,
	},
	{
		header: '공유 수',
		name: 'shareCount',
		width: COLUMN_WIDTHS.shareCount,
		align: 'end',
		prefixed: true,
	},
	{
		header: '인게이지먼트',
		name: 'engagement',
		width: COLUMN_WIDTHS.engagement,
		align: 'end',
		sortable: true,
		prefixed: true,
	},
];

const CreatorRankingTable = ({ code, filter }: ICreatorRankingTable) => {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);
	const [sortField, setSortField] = useState('viewCount');
	const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');
	const [viewMode, setViewMode] = useState('평균');
	const [expandedCreator, setExpandedCreator] = useState<string | null>(null);

	const apiViewMode = VIEW_MODE_MAP[viewMode];
	const headerPrefix = viewMode === '평균' ? '평균' : '누적';

	const HEADER_ITEMS: IHeaderItem[] = BASE_HEADER_ITEMS.map((item) => {
		if (!item.prefixed) return item;
		return { ...item, header: `${headerPrefix} ${item.header}` };
	});

	const apiOrderBy = getCreatorOrderBy(sortField, apiViewMode, sortDirection);

	const params: ICreatorRankingFilterParams = {
		...filter,
		viewMode: apiViewMode,
		orderBy: apiOrderBy,
		page,
		pageSize,
	};

	const { data: response, isLoading } = useGetCreatorRanking(code, params);

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'desc') {
				setSortDirection('asc');
			} else {
				// asc 상태에서 재클릭 → 기본 정렬로 복귀
				setSortField('viewCount');
				setSortDirection('desc');
			}
		} else {
			setSortField(field);
			setSortDirection('desc');
		}
		setPage(1);
	};

	const handleCreatorClick = (item: ITrackingCreatorRankingDB) => {
		const url =
			item.reportLink ??
			`https://app.featuring.co/report/${(item.platformType ?? '').toLowerCase()}/${item.platformPk}`;
		window.open(url, '_blank', 'noopener,noreferrer');
	};

	const handleRowToggle = (influencerCode: string) => {
		setExpandedCreator((prev) => (prev === influencerCode ? null : influencerCode));
	};

	const getSortIndicator = (field: string) => {
		if (sortField === field) {
			return sortDirection === 'desc' ? ' ↓' : ' ↑';
		}
		return '';
	};

	return (
		<div>
			<Grid flex="between" align_items="end" _margin="0 0 16px 0">
				<Text typo="p_bold">전체 크리에이터 {(response?.totalCount ?? pageSize).toLocaleString()}명</Text>
				<Grid flex="end" _gap="8px" _width="auto">
					<CoreRadioChips
						group_type="carousel"
						style_type="gray"
						_width="auto"
						chips_list={VIEW_MODE_OPTIONS}
						selected_chips={viewMode}
						setSelectedChips={setViewMode}
					/>
					<CoreSelect
						select_type="box"
						_width="160px"
						_height="36px"
						option_list={PAGE_SIZE_OPTIONS}
						selected_option={PAGE_SIZE_REVERSE[pageSize]}
						setSelectedOption={(option: string) => {
							setPageSize(PAGE_SIZE_MAP[option] ?? 25);
							setPage(1);
						}}
					/>
				</Grid>
			</Grid>

			<TableHeaderWrapper>
				{HEADER_ITEMS.map((item, idx) => (
					<>
						<HeaderCell
							key={item.name}
							_width={`${item.width}px`}
							flex={item.align ?? 'center'}
							align="center"
							_padding="0 10px"
							$sortable={!!item.sortable}
							onClick={item.sortable ? () => handleSort(item.name) : undefined}
						>
							<Text white_space="pre" typo="span">
								{item.header}
								{item.sortable && getSortIndicator(item.name)}
							</Text>
						</HeaderCell>
						{idx !== HEADER_ITEMS.length - 1 && idx !== 0 && (
							<Contour _height="40%" _width="1px" />
						)}
					</>
				))}
			</TableHeaderWrapper>

			{isLoading ? (
				<Grid flex="center" _height="500px">
					<CoreLoading color_type="black" size_type="large" />
				</Grid>
			) : !response?.creators?.length ? (
				<Grid flex="center" _height="500px">
					<Text typo="p" color="gray">
						데이터가 없습니다.
					</Text>
				</Grid>
			) : (
				response.creators.map((item: ITrackingCreatorRankingDB) => (
					<div key={item.influencerCode}>
						<TableRow
							selected={expandedCreator === item.influencerCode}
							onClick={() => handleRowToggle(item.influencerCode)}
						>
							<TableCell cell_type="center" _width={COLUMN_WIDTHS.expand}>
								<ChevronIcon $expanded={expandedCreator === item.influencerCode}>
									▸
								</ChevronIcon>
							</TableCell>
							<TableCell cell_type="center" _width={COLUMN_WIDTHS.rank}>
								{String(item.rank)}
							</TableCell>
							<TableCell
								cell_type="common"
								_width={COLUMN_WIDTHS.creator}
								_padding="0 10px"
							>
								<CoreTooltip direction="down" text="계정 리포트 바로가기">
									<CreatorCell
										onClick={(e) => {
											e.stopPropagation();
											handleCreatorClick(item);
										}}
									>
										<AvatarWithBadge>
											<AvatarRound>
												<CoreImg
													src={item.profileImageUrl}
													alt=""
													_width={32}
													_height={32}
													error_src="https://featuring-score-web.s3.amazonaws.com/static/images/score/v3/common/cmmn-default-image.png"
													lazy
												/>
											</AvatarRound>
											{PLATFORM_ICON_MAP[item.platformType] && (
												<PlatformBadge>
													<CoreSvg _size="10px">
														{PLATFORM_ICON_MAP[item.platformType]}
													</CoreSvg>
												</PlatformBadge>
											)}
										</AvatarWithBadge>
										<CreatorInfo>
											<CreatorName>{item.nickname ?? item.username}</CreatorName>
										</CreatorInfo>
									</CreatorCell>
								</CoreTooltip>
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.followers}>
								{(item.followerCount ?? 0).toLocaleString()}
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.contentCount}>
								{(item.contentCount ?? 0).toLocaleString()}
							</TableCell>
							<TableCell
								cell_type="common"
								_width={COLUMN_WIDTHS.contentTypes}
								_padding="0 10px"
							>
								<ContentTypeBadges>
									{item.contentTypes.map((ct) => (
										<ContentTypeBadge key={ct}>
											{CONTENT_TYPE_SHORT_MAP[ct] ?? ct}
										</ContentTypeBadge>
									))}
								</ContentTypeBadges>
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.viewCount}>
								{(item.viewCount ?? 0).toLocaleString()}
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.likeCount}>
								{(item.likeCount ?? 0).toLocaleString()}
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.commentCount}>
								{(item.commentCount ?? 0).toLocaleString()}
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.shareCount}>
								{(item.shareCount ?? 0).toLocaleString()}
							</TableCell>
							<TableCell cell_type="number" _width={COLUMN_WIDTHS.engagement}>
								{(item.engagementCount ?? 0).toLocaleString()}
							</TableCell>
						</TableRow>

						{expandedCreator === item.influencerCode && (
							<CreatorContentsAccordion
								code={code}
								influencerCode={item.influencerCode}
								username={item.username}
								contentCount={item.contentCount}
							/>
						)}
					</div>
				))
			)}

			{response && response.totalCount > 0 && (
				<FooterWrapper>
					<SimpleTableFooter
						data={Array(response.totalCount).fill(null) as null[]}
						page={page}
						filter_page={pageSize}
						setPage={setPage}
					/>
				</FooterWrapper>
			)}
		</div>
	);
};

const { Grid, Text } = style;

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart};
`;

const HeaderCell = styled(Grid)<{ $sortable: boolean }>`
	padding: 8px 10px;
	${({ $sortable }) =>
		$sortable &&
		css`
			cursor: pointer;
			&:hover {
				background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
				border-radius: 4px;
			}
			&:hover span {
				color: ${({ theme }) => theme.colors.gray.gray80_text};
			}
		`}
`;

const TableRow = styled(Grid)<{ selected: boolean }>`
	min-width: calc(100% - 32px);
	height: 65px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;
	cursor: pointer;

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	${({ theme, selected }) =>
		selected &&
		css`
			border: 1px solid ${theme.colors.uplus.primary10_lighten};
		`}

	&:hover {
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		${({ theme, selected }) =>
			selected &&
			css`
				border-color: ${theme.colors.uplus.primary50_key};
			`}
	}
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
`;

const CreatorCell = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 6px;
	border-radius: 6px;
	cursor: pointer;

	&:hover {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	}
`;

const AvatarWithBadge = styled.div`
	position: relative;
	width: 32px;
	height: 32px;
	flex-shrink: 0;
`;

const AvatarRound = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	overflow: hidden;
`;

const PlatformBadge = styled.div`
	position: absolute;
	bottom: -2px;
	right: -2px;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	background: white;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
`;

const CreatorInfo = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const CreatorName = styled.span`
	${({ theme }) => theme.typo.body.span_bold};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	&:hover {
		text-decoration: underline;
	}
`;

const ChevronIcon = styled.span<{ $expanded: boolean }>`
	display: inline-block;
	font-size: 14px;
	color: ${({ theme }) => theme.colors.gray.gray50};
	transition: transform 0.15s;
	transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'none')};
`;

const ContentTypeBadges = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
`;

const ContentTypeBadge = styled.span`
	${({ theme }) => theme.typo.body.span};
	background: ${({ theme }) => theme.colors.gray.gray05_bg};
	border-radius: 4px;
	padding: 2px 6px;
	white-space: nowrap;
`;

const FooterWrapper = styled.div`
	margin-top: 16px;
`;

export default CreatorRankingTable;
